import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ReportCard } from '@components/Pages/AnalyticsPage/ReportCard';
import { GroupedSelect } from '@components/ui/GroupedSelect/GroupedSelect';
import { DateField } from '@components/ui/DateField/DateField';
import { useSiteAreas } from '@hooks/useSiteAreas';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { APP_DEFAULT_TZ } from '@utils/dateUtils';
import type { IInvestigationReportLocation } from '@apis/investigationReports';
import { CardIcon } from '../CardIcon';
import { todayCap } from '../utils/dateRules';
import { buildAreaOptions, expandSelection } from './ObserveLocationCard.helpers.js';

export interface IObserveLocationSubmission {
  locations: IInvestigationReportLocation[];
  date: Date;
}

interface IObserveLocationCardProps {
  isSelected: boolean;
  onSelect: (name: 'observeLocation') => void;
  onSubmit: (payload: IObserveLocationSubmission) => void;
}

export function ObserveLocationCard(props: IObserveLocationCardProps) {
  const { isSelected, onSelect, onSubmit } = props;
  const { t } = useTranslation();
  const { siteId = '' } = useParams<{ siteId: string }>();
  const tz = useSelector(selectSiteTz) ?? APP_DEFAULT_TZ;
  const areasQuery = useSiteAreas(siteId);
  const [selectionKey, setSelectionKey] = useState<string | null>(null);
  const [date, setDate] = useState<Date | null>(null);

  const grouped = useMemo(() => {
    const opts = buildAreaOptions(areasQuery.data ?? []);
    const byGroup = new Map<
      string,
      { groupId: string; groupLabel: string; options: { id: string; label: string }[] }
    >();
    for (const o of opts) {
      const g = byGroup.get(o.groupId) ?? {
        groupId: o.groupId,
        groupLabel: o.groupLabel,
        options: [],
      };
      g.options.push({ id: o.selectionKey, label: o.label });
      byGroup.set(o.groupId, g);
    }
    return Array.from(byGroup.values());
  }, [areasQuery.data]);

  const canSubmit = selectionKey !== null && date !== null;

  return (
    <ReportCard
      name="observeLocation"
      title={t('analytics.cards.observeLocation.title')}
      description={t('analytics.cards.observeLocation.description')}
      icon={<CardIcon name="pin" />}
      isSelected={isSelected}
      onSelect={() => onSelect('observeLocation')}
      onSubmit={() => {
        if (!selectionKey || !date) return;
        const locations = expandSelection(selectionKey, areasQuery.data ?? []);
        if (locations.length === 0) return;
        onSubmit({ locations, date });
      }}
      submitLabel={t('analytics.createButton')}
      submitDisabled={!canSubmit || areasQuery.isLoading}
    >
      <GroupedSelect
        value={selectionKey}
        onChange={setSelectionKey}
        groups={grouped}
        placeholder={t('analytics.filters.selectPlaceholder')}
        ariaLabel={t('analytics.filters.area')}
      />
      <DateField
        value={date}
        onChange={setDate}
        placeholder={t('analytics.filters.selectPlaceholder')}
        ariaLabel={t('analytics.filters.dateSingle')}
        maxDate={todayCap(tz)}
        disabled={!selectionKey}
      />
    </ReportCard>
  );
}
